import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, MapPin, CalendarClock, User2, Loader2, XCircle, HeartPulse, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

/** Public kiosk page: employee scans QR, enters Emp ID + DOB, attendance is logged.
 * URL: /kiosk/event/:eventId
 */
export default function EventKiosk() {
  const { eventId } = useParams();
  const { branding } = useBranding();
  const brand = branding?.theme_color || "#0EA5E9";

  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [empId, setEmpId] = useState("");
  const [dob, setDob] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { already_marked, attended_at, employee, event }

  useEffect(() => {
    api.get(`/public/events/kiosk/${eventId}`)
      .then(r => setInfo(r.data))
      .catch(() => setNotFound(true));
  }, [eventId]);

  async function submit(e) {
    e?.preventDefault?.();
    if (!empId.trim()) { toast.error("Enter your Employee ID"); return; }
    if (!dob.trim()) { toast.error("Enter your Date of Birth"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/public/events/kiosk/attend", { event_id: eventId, emp_id: empId.trim(), dob: dob.trim() });
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setSubmitting(false);
    }
  }

  function resetForNext() {
    setEmpId("");
    setDob("");
    setResult(null);
  }

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <Card className="max-w-md w-full border-slate-200 rounded-3xl">
          <CardContent className="p-8 text-center space-y-3">
            <XCircle className="h-12 w-12 text-red-500 mx-auto"/>
            <div className="font-display font-black text-2xl text-slate-900">Event not found</div>
            <p className="text-slate-500">This QR code is invalid or the event has been removed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500"/>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white p-4 sm:p-8">
      <div className="max-w-md mx-auto space-y-5">
        {/* Header */}
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-white shadow-sm border border-slate-200 px-4 py-1.5">
            <HeartPulse className="h-4 w-4" style={{ color: brand }}/>
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Attendance Kiosk</span>
          </div>
        </div>

        {/* Event card */}
        <Card className="border-slate-200 rounded-3xl overflow-hidden">
          <div className="h-2" style={{ background: brand }}/>
          <CardContent className="p-6 space-y-3" data-testid="kiosk-event-info">
            <div className="font-display font-black text-2xl text-slate-900">{info.title}</div>
            {info.description && <p className="text-sm text-slate-600">{info.description}</p>}
            <div className="grid grid-cols-1 gap-2 text-sm text-slate-700 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-slate-400"/>
                {new Date(info.start_datetime).toLocaleString()}
              </div>
              {info.venue && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400"/>{info.venue}</div>}
              {info.trainer_name && <div className="flex items-center gap-2"><User2 className="h-4 w-4 text-slate-400"/>{info.trainer_name}</div>}
              {info.hospital && <div className="text-xs text-slate-500 font-mono">{info.hospital.name}{info.hospital.code ? ` · ${info.hospital.code}` : ""}</div>}
            </div>
          </CardContent>
        </Card>

        {/* Form / Result */}
        {result ? (
          <Card className="border-emerald-200 bg-emerald-50 rounded-3xl">
            <CardContent className="p-6 text-center space-y-4" data-testid="kiosk-result">
              <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto"/>
              <div>
                <div className="font-display font-black text-2xl text-slate-900">
                  {result.already_marked ? "Already marked" : "Attendance recorded"}
                </div>
                <div className="text-slate-600 mt-1">
                  {result.employee.name} <span className="text-slate-400 font-mono">({result.employee.emp_id})</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {new Date(result.attended_at).toLocaleString()}
                </div>
              </div>
              <Button onClick={resetForNext} className="w-full h-12 rounded-2xl text-white font-bold" style={{ background: brand }} data-testid="kiosk-next-btn">
                Mark next employee
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200 rounded-3xl">
            <CardContent className="p-6">
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Employee ID</Label>
                  <Input value={empId} onChange={e => setEmpId(e.target.value)} placeholder="e.g. EMP001" className="mt-1 h-12" autoFocus data-testid="kiosk-empid-input"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Date of Birth</Label>
                  <Input value={dob} onChange={e => setDob(e.target.value)} placeholder="DD/MM/YYYY" className="mt-1 h-12" data-testid="kiosk-dob-input"/>
                </div>
                <Button type="submit" disabled={submitting} className="w-full h-12 rounded-2xl text-white font-bold text-lg" style={{ background: brand, boxShadow: `0 10px 30px -12px ${brand}` }} data-testid="kiosk-submit-btn">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin"/> : <><ClipboardCheck className="h-5 w-5 mr-2"/>Mark Attendance</>}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
